import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Inspiration, InspirationFormat } from '../../models/inspiration.interface';
import * as InspirationActions from './inspiration.actions';
import { InspirationAction } from './inspiration.actions';
const cache = new Map();
@Injectable()
export class InspirationEffects {
  constructor(
    private actions$: Actions<InspirationAction>,
    private http: HttpClient,
    private router: Router
  ) {}

  public GetAllInspirations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_ALL_INSPIRATIONS, InspirationActions.LOAD_MORE_INSPIRATIONS),
      switchMap((action: InspirationActions.GetAllInspirations | InspirationActions.LoadMoreInspirations) => {
        const payload = action.payload;

        let url = '?';
        url += payload.page ? `page=${payload.page}` : '';
        url += payload.pageSize ? `&page_size=${payload.pageSize}` : '';
        url += payload.tagModule ? `&tag_module=${payload.tagModule}` : '';
        url += payload.filters
          ? payload.filters
              .filter((filter) => filter?.value !== 'null')
              .map((filter) => `&${filter.name}=${filter.value}`)
              .join('')
          : '';

        return this.http.get<PaginatedApiResponse<Inspiration>>(ApiEndpoint.INSPIRATIONS + url);
      }),
      map((inspirations) => new InspirationActions.GetAllInspirationsSuccess(inspirations)),
      catchError(() =>
        of(
          new InspirationActions.GetAllInspirationsFailure({
            results: [],
            loading: false,
            page_size: 0,
            count: 0,
            current: 0,
            next: '',
            previous: '',
          })
        )
      )
    )
  );

  public GetFormatInspirations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_FEATURED_FORMAT_INSPIRATIONS),
      mergeMap((action: InspirationActions.GetFeaturedFormatInspirations) => {
        const payload = action.payload;
        const serialAction = JSON.stringify(action);
        let url = '?';
        url += payload.page ? `page=${payload.page}` : '';
        url += payload.pageSize ? `&page_size=${payload.pageSize}` : '';
        url += payload.tagModule ? `&tag_module=${payload.tagModule}` : '';
        url += payload.format ? `&inspiration_format=${payload.format}` : '';
        if (!cache.has(serialAction)) {
          return this.http.get<PaginatedApiResponse<Inspiration>>(ApiEndpoint.INSPIRATIONS + url).pipe(
            map((inspirations) => {
              cache.set(serialAction, inspirations);
              return new InspirationActions.GetFeaturedFormatInspirationsSuccess(inspirations, payload.format);
            })
          );
        } else {
          return of(
            new InspirationActions.GetFeaturedFormatInspirationsSuccess(
              cache.get(serialAction) as PaginatedApiResponse<Inspiration>,
              payload.format
            )
          );
        }
      })
    )
  );

  public GetInspirationFormats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_INSPIRATION_FORMATS),
      switchMap(() =>
        this.http.get<Array<InspirationFormat>>(ApiEndpoint.INSPIRATION_FORMATS + '?ordering=-id&has_content=true')
      ),
      map((formats) => new InspirationActions.GetInspirationFormatsSuccess(formats))
    )
  );

  public GetArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_ARTICLE),
      switchMap((action: InspirationActions.GetArticle) => {
        return this.http.get<any>(ApiEndpoint.ARTICLES + action.payload).pipe(
          map((article) => new InspirationActions.GetArticleSuccess(article)),
          catchError((error) => {
            this.router.navigate(['/error/not-found']);
            return of(new InspirationActions.GetArticleFail(error));
          })
        );
      })
    )
  );

  public GetPreloginArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_PRELOGIN_ARTICLE),
      switchMap((action: InspirationActions.GetPreloginArticle) => {
        return this.http.get<any>(ApiEndpoint.PRELOGIN_ARTICLES + action.payload).pipe(
          map((article) => new InspirationActions.GetArticleSuccess(article)),
          catchError((error) => {
            this.router.navigate(['home']);
            return of(new InspirationActions.GetArticleFail(error));
          })
        );
      })
    )
  );

  public GetPublication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_PUBLICATION),
      switchMap((action: InspirationActions.GetPublication) => {
        return this.http.get<any>(ApiEndpoint.PUBLICATIONS + action.payload).pipe(
          map((article) => new InspirationActions.GetPublicationSuccess(article)),
          catchError((error) => {
            this.router.navigate(['/error/not-found']);
            return of(new InspirationActions.GetPublicationFail(error));
          })
        );
      })
    )
  );

  public GetPreloginPublication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_PRELOGIN_PUBLICATION),
      switchMap((action: InspirationActions.GetPreloginPublication) => {
        return this.http.get<any>(ApiEndpoint.PRELOGIN_PUBLICATIONS + action.payload).pipe(
          map((article) => new InspirationActions.GetPublicationSuccess(article)),
          catchError((error) => {
            this.router.navigate(['home']);
            return of(new InspirationActions.GetPublicationFail(error));
          })
        );
      })
    )
  );

  public GetFeaturedInspirations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.GET_FEATURED_INSPIRATIONS),
      switchMap((action: InspirationActions.GetFeaturedInspirations) => {
        const payload = action.payload;

        let url = '?featured=true';
        url += payload.page ? `&page=${payload.page}` : '';
        url += payload.pageSize ? `&page_size=${payload.pageSize}` : '';
        url += payload.tagModule ? `&tag_module=${payload.tagModule}` : '';

        return this.http.get<PaginatedApiResponse<Inspiration>>(ApiEndpoint.INSPIRATIONS + url);
      }),
      map((inspirations) => new InspirationActions.GetFeaturedInspirationsSuccess(inspirations))
    )
  );

  public redirectArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InspirationActions.REDIRECT_ARTICLE),
      switchMap((action: InspirationActions.RedirectArticle) => {
        return this.http.get<any>(ApiEndpoint.ARTICLES + action.payload).pipe(
          catchError((error) => {
            this.router.navigate(['/error/not-found']);
            return of(new InspirationActions.GetArticleFail(error));
          })
        );
      }),
      map((article) => new InspirationActions.GetArticleSuccess(article)),
      tap((article) => {
        this.router.navigateByUrl('/inspiration/article/' + article.payload.slug);
      })
    )
  );
}

export const inspirationEffects = [InspirationEffects];
